import { ComponentEntity, stringifyEntityRef } from '@backstage/catalog-model';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { MessagingResource, MessagingTopology } from '@internal/open-catalog';
import { Backdrop, Button, Card, CardContent, CircularProgress, createStyles, Dialog, Divider, FormControlLabel, IconButton, InputAdornment, ListItem, ListItemIcon, ListItemText, makeStyles, Slide, Switch, TextField, Theme, Typography } from '@material-ui/core';
import BackupIcon from '@material-ui/icons/Backup';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useState } from 'react';
import SimpleConfirmDialog from '../../../../../../components/dialog';
import { ButtonExportTopology } from '../rabbitmq';
import { TopologyEdit } from './topology.edit';
import { useDebounce } from 'react-use';
import { debugNameFilterOut } from '@internal/backstage-plugin-amqp-topology-manager-common';
import { useNotification } from '../../../../../../hooks/useNotification';
import { NotificationSnackbar } from '../../../../../../components/notification-snackbar/notification-snackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: { marginTop: '1em' },
    header: {
      alignItems: 'center',
      padding: 16,
      paddingBottom: 0,
    },
    headerForm: {
      alignItems: 'center',
      '& > *': {
        margin: theme.spacing(1),
        minWidth: 175,
        minHeight: 40,
      },
    },
    fullScreen: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      width: '100vw',
      zIndex: 10000,
      '& div[class|="MuiAccordionDetails"]': {
        maxHeight: '70vh',
      },
    },
    iconButtonLabel: {
      paddingLeft: theme.spacing(1),
      textTransform: 'capitalize',
    },
  }),
);

const buildDraft = (
  service: ComponentEntity,
  resource: MessagingResource,
): MessagingTopology => {
  const MESSAGING_CHANNEL_NAME = 'open.totvs.io/messaging-channel-name';

  const topology: MessagingTopology = resource.spec?.topology
    ? JSON.parse(JSON.stringify(resource.spec.topology))
    : ({} as MessagingTopology);

  const { name: serviceName } = service.metadata;

  const channelName =
    service.metadata.annotations?.[MESSAGING_CHANNEL_NAME] || serviceName;

  const defaults = {
    input: () => ({
      name: `${channelName}-input`,
      type: `headers`,
      sources: [],
      targets: [],
      arguments: {},
    }),
    output: () => ({
      name: `${channelName}-output`,
      type: `headers`,
      targets: [],
      arguments: {},
    }),
    parkingLot: () => ({
      name: `${channelName}-input.parking-lot`,
      type: `classic`,
      arguments: {},
    }),
  };

  topology.input = topology.input || defaults.input();
  topology.output = topology.output || defaults.output();
  topology.parkingLot = topology.parkingLot || defaults.parkingLot();

  topology.channelName = topology.channelName || channelName;

  topology.input.targets = topology.input.targets.filter(debugNameFilterOut);
  topology.input.sources = topology.input.sources.filter(debugNameFilterOut);
  topology.output.targets = topology.output.targets.filter(debugNameFilterOut);

  return topology;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const TopologyEditDialog = (props: {
  open: boolean;
  onClose: () => void;
  service: ComponentEntity;
  loadResources: () => Promise<MessagingResource[]>;
  entity: ComponentEntity;
}) => {
  const classes = useStyles();
  const { open, onClose, service, entity, loadResources: resources } = props;

  const smartTalkApplicationId: string | undefined =
    entity.metadata.annotations?.['open.totvs.io/smart-talk-id'];

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [draft, setDraft] = useState<MessagingTopology>();
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('');
  const [focusMode, setFocusMode] = useState(true);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { notification, showError, hideNotification } = useNotification();
  

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue.toLowerCase());
    },
    300,
    [searchValue],
  );

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value.toLowerCase());
  };

  const handleFocusModeChange = () => {
    setFocusMode(!focusMode);
  };

  const handleSubmit = async () => {
    if (!draft) return;
    if (isSubmitting) return;

    setIsSubmitting(true);

    discoveryApi
      .getBaseUrl('amqp-topology-manager')
      .then(url => {
        return fetchApi.fetch(`${url}/drafts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            component_entity_ref: stringifyEntityRef(service),
            system_entity_ref: stringifyEntityRef(entity),
            data: JSON.stringify(draft),
          }),
        });
      })
      .then((response) => {
        setIsSubmitting(false);
        if(!response) return;
        if (!response.ok) {
          showError(`Failed to submit draft: ${response.statusText}`);
          return;
        }
        onClose();
      });
  };

  useEffect(() => {
    if (!open) return;
    const serviceRef = stringifyEntityRef(service);

    discoveryApi
      .getBaseUrl('amqp-topology-manager')
      .then(base => {
        const url = new URL(`${base}/drafts/component`);
        url.searchParams.append('componentEntityRef', serviceRef);
        return fetchApi.fetch(url);
      })
      .then(response => {
        if (!response.ok) throw new Error('Draft not found.');
        return response.json();
      })
      .then(draftView => {
        const draftData = JSON.parse(draftView.data) as MessagingTopology;
        setDraft(draftData);
      })
      .catch(async () => {
        // 404 or error on parsing response from draft api
        // always fetch the resources from the backend
        try {
          const resolvedResources = await resources();
          const resource = resolvedResources
            .filter(r => r.spec.dependencyOf.includes(serviceRef))
            .find(r => r.spec.environment === 'development');

          const builtDraft = buildDraft(
            service,
            resource || ({} as MessagingResource),
          );
          setDraft(builtDraft);

        } catch (error) {
          /* ignore */
        }
      });
  }, [service, resources, discoveryApi, fetchApi, open]);

  const handleClose = () => {
    setConfirmCloseOpen(true);
  };

  return (
    <>
      <Dialog fullScreen open={open} TransitionComponent={Transition}>
        <Card className={classes.fullScreen}>
          <ListItem className={classes.header}>
            <ListItemText secondary="Please, submit this draft when finished or the changes will be lost when this page is refreshed.">
              <Typography variant="h5">
                {service.metadata.name.toLocaleUpperCase()}
              </Typography>
            </ListItemText>

            <TextField
              label="Search"
              value={searchValue}
              onChange={e => {
                handleSearchValueChange(e.target.value);
              }}
              variant="outlined"
              size="small"
              placeholder="ex: orders"
              style={{ marginRight: 16 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleSearchValueChange('')}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Switch checked={focusMode} onChange={handleFocusModeChange} />
              }
              style={{ marginLeft: 8 }}
              label="Focus mode"
              title="Toggle view the infrastructure components generated by the platform."
            />

            {draft && (
              <>
                <ButtonExportTopology topology={draft} suffix="draft" />

                <ListItemIcon>
                  <Button onClick={handleSubmit} title="Submit changes">
                    <BackupIcon color="secondary" />
                    <Typography className={classes.iconButtonLabel}>
                      Submit
                    </Typography>
                  </Button>
                </ListItemIcon>
              </>
            )}

            <ListItemIcon>
              <Button
                onClick={handleClose}
                children={<CloseIcon />}
                title="Close edit window."
              />
            </ListItemIcon>
          </ListItem>

          <Divider className={classes.divider} />

          <CardContent>
            {draft ? (
              <TopologyEdit
                topology={draft}
                focusMode={focusMode}
                searchValue={debouncedSearchValue}
                smartTalkApplicationId={smartTalkApplicationId}
              />
            ) : (
              <Typography>Loading topology draft.</Typography>
            )}
          </CardContent>

          <Backdrop
            open={isSubmitting}
            style={{ zIndex: 20000, color: '#fff' }}
          >
            <CircularProgress color="inherit" />
            <Typography style={{ marginLeft: 12 }}>
              Submitting draft...
            </Typography>
          </Backdrop>
          <NotificationSnackbar notification={notification} onClose={hideNotification} />
        </Card>
      </Dialog>
      <SimpleConfirmDialog
        open={confirmCloseOpen}
        title="Data loss risk"
        text="You may have unsaved changes, do you want to continue?"
        onCancel={() => setConfirmCloseOpen(false)}
        onConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
      />
    </>
  );
};
